import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Typography, Space } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import cognitoService from '../lib/cognito';

const { Text, Title } = Typography;

export const VerifyCode: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get phone and email from navigation state
    const statePhone = (location.state as any)?.phone;
    const stateEmail = (location.state as any)?.email;
    if (statePhone) {
      setPhone(statePhone);
    }
    if (stateEmail) {
      setEmail(stateEmail);
    }
  }, [location.state]);

  const onFinish = async (values: { phone?: string; code: string }) => {
    const phoneToUse = phone || values.phone;
    if (!phoneToUse) {
      message.error('Please enter your phone number');
      return;
    }

    // Format phone number
    let phoneNumber = phoneToUse.trim();
    if (!phoneNumber.startsWith('+')) {
      phoneNumber = '+' + phoneNumber;
    }

    setLoading(true);

    try {
      const result = await cognitoService.confirmSignUp(phoneNumber, values.code);

      if (result.success) {
        message.success('Account verified! You can now log in.');
        navigate('/login', { state: { verified: true } });
      } else {
        message.error(result.error || 'Verification failed');
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      message.error(error.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!phone) {
      message.error('Please enter your phone number first');
      return;
    }

    // Format phone number
    let phoneNumber = phone.trim();
    if (!phoneNumber.startsWith('+')) {
      phoneNumber = '+' + phoneNumber;
    }

    setResending(true);

    try {
      const result = await cognitoService.resendConfirmationCode(phoneNumber);

      if (result.success) {
        message.success('Verification code sent! Check your email.');
      } else {
        message.error(result.error || 'Failed to resend code');
      }
    } catch (error: any) {
      console.error('Resend error:', error);
      message.error(error.message || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '20px',
      background: 'linear-gradient(135deg, #003874 0%, #94AFCC 100%)'
    }}>
      <Card
        style={{ width: 420, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src="/logo.png" alt="ShadesParking" style={{ height: 96, marginBottom: 16 }} />
          <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>Verify Your Account</Title>
          <Text type="secondary">
            We've sent a 6-digit verification code to your email.
            {email && <><br />({email})</>}
          </Text>
        </div>

        <Form
          name="verify"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          initialValues={{ phone }}
        >
          {!phone && (
            <Form.Item
              name="phone"
              rules={[
                { required: true, message: 'Please input your phone number!' },
                { pattern: /^\+?[0-9]{10,15}$/, message: 'Please enter a valid phone number' }
              ]}
            >
              <Input
                prefix={<PhoneOutlined />}
                placeholder="Phone (e.g. +971501234567)"
                onChange={(e) => setPhone(e.target.value)}
              />
            </Form.Item>
          )}

          <Form.Item
            name="code"
            rules={[
              { required: true, message: 'Please input verification code!' },
              { len: 6, message: 'Code must be 6 digits' }
            ]}
          >
            <Input
              placeholder="Enter 6-digit code"
              maxLength={6}
              style={{ textAlign: 'center', fontSize: 18, letterSpacing: 8 }}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Verify
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Space direction="vertical" size="small">
              <div>
                <Text type="secondary">Didn't receive the code? </Text>
                <Button type="link" onClick={handleResendCode} loading={resending} style={{ padding: 0 }}>
                  Resend Code
                </Button>
              </div>
              <Link to="/login">Back to Login</Link>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};
